import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import type { UnengagedConversationProof } from "../types/core";

interface Args {
  organizationId: string | null;
  setterId: string | null; // pass null when modal closed; pass real id or "__UNASSIGNED__" when open
  dateRange: { start: Date | null; end: Date | null };
  enabled: boolean;
  limit?: number;
  offset?: number;
}

export function useUnengagedProof({
  organizationId,
  setterId,
  dateRange,
  enabled,
  limit = 25,
  offset = 0,
}: Args) {
  const [rows, setRows] = useState<UnengagedConversationProof[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startISO = dateRange.start?.toISOString() ?? null;
  const endISO = dateRange.end?.toISOString() ?? null;

  useEffect(() => {
    if (!enabled || !organizationId || !setterId || !startISO || !endISO || !supabase) {
      setRows([]); // eslint-disable-line react-hooks/set-state-in-effect -- clear stale state when modal disabled
      setTotalCount(0);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.resolve(
      supabase.rpc("get_setter_unengaged_proof", {
        p_organization_id: organizationId,
        p_setter_id: setterId,
        p_start_date: startISO,
        p_end_date: endISO,
        p_limit: limit,
        p_offset: offset,
      })
    )
      .then(({ data, error: rpcError }) => {
        if (cancelled) return;
        if (rpcError) {
          setError(rpcError.message);
          setRows([]);
          setTotalCount(0);
        } else {
          const mapped: UnengagedConversationProof[] = (data ?? []).map((r: any) => ({
            conversation_id: r.conversation_id,
            contact_name: r.contact_name,
            lead_instagram: r.lead_instagram,
            first_lead_msg_date: r.first_lead_msg_date,
            last_lead_msg_at: r.last_lead_msg_at,
            last_message_text: r.last_message_text,
            days_since_lead_first_reached_out: r.days_since_lead_first_reached_out,
            total_count: r.total_count,
          }));
          setRows(mapped);
          setTotalCount(mapped[0]?.total_count ?? 0);
        }
      })
      .catch((e: { message?: string }) => {
        if (cancelled) return;
        setError(e?.message ?? "unknown error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, setterId, startISO, endISO, enabled, limit, offset]);

  return { rows, totalCount, loading, error };
}
